import React, { useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/ar';
import 'moment/locale/en-gb';
import { theme } from '../../../theme';
import Can from '../../../permissions/Can';
import MarkAttendance from './MarkAttendance';
import { useTranslation } from 'react-i18next';

const WeekView = ({
  appointments = [],
  currentDate,
  onShowDetails,
  onEditAppointment,
  onDeleteAppointment,
  isEmployee = false,
}) => {
  const { t, i18n } = useTranslation();

  // Configure moment locale immediately and on language change
  const setMomentLocale = () => {
    const momentLocale = i18n.language === 'ar' ? 'ar' : i18n.language === 'en' ? 'en-gb' : 'fr';
    moment.locale(momentLocale);
    
    // For Arabic, use Latin numerals instead of Arabic numerals
    if (i18n.language === 'ar') {
      moment.updateLocale(momentLocale, {
        preparse: function (string) {
          return string.replace(/[٠-٩]/g, function (match) {
            return match.charCodeAt(0) - 0x0660;
          });
        },
        postformat: function (string) {
          return string.replace(/\d/g, function (match) {
            return String.fromCharCode(match.charCodeAt(0) + 0x0660);
          });
        }
      });
      // Override postformat to keep Latin numerals
      moment.updateLocale(momentLocale, {
        postformat: function (string) {
          return string; // Keep original string with Latin numerals
        }
      });
    }
  };
  
  // Set locale immediately on mount
  setMomentLocale();
  
  // Also update locale when language changes
  useEffect(() => {
    setMomentLocale();
  }, [i18n.language]);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileDialogOpen, setMobileDialogOpen] = React.useState(false);
  const [selectedDayAppointments, setSelectedDayAppointments] = React.useState([]);
  const [selectedAppointmentForAction, setSelectedAppointmentForAction] = React.useState(null);

  const weekDays = useMemo(() => {
    // For Arabic: week starts on Sunday, for others: week starts on Monday (ISO)
    const start = i18n.language === 'ar' 
      ? currentDate.clone().startOf('week') // Sunday
      : currentDate.clone().startOf('isoWeek'); // Monday
    return Array.from({ length: 7 }, (_, i) => start.clone().add(i, 'days'));
  }, [currentDate, i18n.language]);

  // Week day order: Sunday to Saturday for Arabic, Monday to Sunday for others
  const weekDayKeys = i18n.language === 'ar'
    ? ['pro_dashboard.week_days.sunday', 'pro_dashboard.week_days.monday', 'pro_dashboard.week_days.tuesday', 'pro_dashboard.week_days.wednesday', 'pro_dashboard.week_days.thursday', 'pro_dashboard.week_days.friday', 'pro_dashboard.week_days.saturday']
    : ['pro_dashboard.week_days.monday', 'pro_dashboard.week_days.tuesday', 'pro_dashboard.week_days.wednesday', 'pro_dashboard.week_days.thursday', 'pro_dashboard.week_days.friday', 'pro_dashboard.week_days.saturday', 'pro_dashboard.week_days.sunday'];

  const handleMobileDayClick = (dayAppointments) => {
    // Si c'est un seul RDV cliqué, afficher tous les RDV du même jour
    if (dayAppointments.length === 1) {
      const clickedAppointment = dayAppointments[0];
      const dayKey = moment(clickedAppointment.start_time).format('YYYY-MM-DD');
      const allAppointmentsOfDay = appointments.filter(a => 
        moment(a.start_time).format('YYYY-MM-DD') === dayKey
      );
      setSelectedDayAppointments(allAppointmentsOfDay);
      setSelectedAppointmentForAction(allAppointmentsOfDay[0] || null);
    } else {
      setSelectedDayAppointments(dayAppointments);
      setSelectedAppointmentForAction(dayAppointments[0] || null);
    }
    setMobileDialogOpen(true);
  };

  const START_HOUR = 6;
  const END_HOUR = 21;
  const hours = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => i + START_HOUR);
  const HOUR_HEIGHT = 60;

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return theme.colors.success[500];
      case 'pending': return theme.colors.warning[500];
      case 'cancelled': return theme.colors.error[500];
      case 'completed': return theme.colors.info[500];
      default: return theme.colors.primary[500];
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return t('pro_dashboard.status.confirmed');
      case 'pending': return t('pro_dashboard.status.pending');
      case 'cancelled': return t('pro_dashboard.status.cancelled');
      case 'completed': return t('pro_dashboard.status.completed');
      default: return status;
    }
  };

  const appointmentsByDay = useMemo(() => {
    const grouped = {};
    weekDays.forEach(d => grouped[d.format('YYYY-MM-DD')] = []);
    appointments.forEach(a => {
      const start = moment(a.start_time);
      const end = moment(a.end_time);
      const key = start.format('YYYY-MM-DD');
      if (!grouped[key] || !start.isValid() || !end.isValid()) return;
      const duration = end.diff(start, 'minutes');
      const top = ((start.hour() - START_HOUR) + start.minute() / 60) * HOUR_HEIGHT;
      const height = Math.max((duration / 60) * HOUR_HEIGHT, 24);
      grouped[key].push({ ...a, topPosition: top, height, durationMinutes: duration });
    });
    return grouped;
  }, [appointments, weekDays]);

  // Vue mobile compacte
  if (isMobile) {
    return (
      <Box sx={{ backgroundColor: '#fff', borderRadius: 3, overflow: 'auto', border: `1px solid ${theme.colors.primary[100]}`, height: 'calc(100vh - 280px)' }}>
        {/* EN-TÊTE */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', backgroundColor: theme.colors.primary[100], borderBottom: `2px solid ${theme.colors.primary[200]}` }}>
          {weekDays.map((d, idx) => {
            const today = d.isSame(moment(), 'day');
            return (
              <Box key={d.format('YYYY-MM-DD')} sx={{ p: 1, textAlign: 'center', backgroundColor: today ? theme.colors.primary[100] : theme.colors.primary[50], borderRight: `1px solid ${theme.colors.primary[100]}`, '&:last-child': { borderRight: 'none' } }}>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.55rem', md: '0.7rem' }, textTransform: 'none', color: theme.colors.text.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t(weekDayKeys[idx])}</Typography>
                <Typography sx={{ fontWeight: today ? 800 : 600, fontSize: '0.85rem', color: today ? theme.colors.primary[700] : theme.colors.text.primary }}>{d.format('D')}</Typography>
              </Box>
            );
          })}
        </Box>

        {/* GRILLE MOBILE */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {weekDays.map(d => {
            const key = d.format('YYYY-MM-DD');
            const dayAppointments = appointmentsByDay[key] || [];
            const today = d.isSame(moment(), 'day');
            return (
              <Box
                key={key}
                sx={{
                  minHeight: '100px',
                  p: 1,
                  borderRight: `1px solid ${theme.colors.primary[100]}`,
                  borderBottom: `1px solid ${theme.colors.primary[100]}`,
                  backgroundColor: today ? theme.colors.primary[50] : '#fff',
                  '&:nth-of-type(7n)': { borderRight: 'none' }
                }}
              >
                {dayAppointments.length > 0 ? (
                  <Box
                    onClick={() => handleMobileDayClick(dayAppointments)}
                    sx={{
                      backgroundColor: theme.colors.primary[500],
                      color: 'white',
                      borderRadius: 1,
                      p: 0.5,
                      textAlign: 'center',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: theme.colors.primary[600],
                      }
                    }}
                  >
                    {dayAppointments.length} {t('pro_dashboard.appointments.short_title')}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '0.7rem', color: theme.colors.text.secondary, textAlign: 'center' }}>
                    -
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Dialog mobile */}
        <Dialog
          open={mobileDialogOpen}
          onClose={() => {
            setMobileDialogOpen(false);
            setSelectedAppointmentForAction(null);
          }}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-container': {
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .MuiDialog-paper': {
              m: { xs: 0, md: 2 },
              maxHeight: { xs: '100%', md: 'calc(100% - 64px)' },
              width: { xs: '100%', md: 'auto' },
              maxWidth: { xs: '100%', md: '600px' },
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 700, 
            borderBottom: `2px solid ${theme.colors.primary[100]}`,
          }}>
            {t('pro_dashboard.appointments.day_appointments')}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {selectedDayAppointments.map((appointment, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  border: `2px solid ${getStatusColor(appointment.status)}`,
                  backgroundColor: '#f9f9f9',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: theme.colors.primary[50],
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }
                }}
              >
                {/* Boutons d'action en haut à droite */}
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                  <MarkAttendance 
                    appointment={appointment} 
                    onUpdate={() => {
                      setMobileDialogOpen(false);
                      setSelectedAppointmentForAction(null);
                      window.location.reload();
                    }} 
                  />
                  {!isEmployee && (
                    <>
                      <Tooltip title={t('actions.edit')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditAppointment?.(appointment);
                            setMobileDialogOpen(false);
                            setSelectedAppointmentForAction(null);
                          }}
                          sx={{
                            color: theme.colors.primary[500],
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: theme.colors.primary[500],
                              color: '#fff',
                            }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('actions.delete')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteAppointment?.(appointment);
                            setMobileDialogOpen(false);
                            setSelectedAppointmentForAction(null);
                          }}
                          sx={{
                            color: theme.colors.error[500],
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: theme.colors.error[500],
                              color: '#fff',
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
                
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, pr: 8 }}>
                  {moment(appointment.start_time).format('HH:mm')} - {appointment.client_name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t('pro_dashboard.services.title')}:</strong> {appointment.service_name}
                </Typography>
                {appointment.team_member_name && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{t('pro_dashboard.team.title')}:</strong> {appointment.team_member_name}
                  </Typography>
                )}
                {appointment.salon_name && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{t('pro_dashboard.salon.title')}:</strong> {appointment.salon_name}
                  </Typography>
                )}
                <Chip
                  label={getStatusLabel(appointment.status)}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(appointment.status),
                    color: 'white',
                    mt: 1,
                  }}
                />
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setMobileDialogOpen(false);
              setSelectedAppointmentForAction(null);
            }}>{t('actions.close')}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Vue desktop - grille horaire
  return (
    <Box sx={{ backgroundColor: '#fff', borderRadius: 3, overflow: 'auto', border: `1px solid ${theme.colors.primary[100]}`, height: 'calc(100vh - 280px)' }}>
      {/* EN-TÊTE */}
      <Box sx={{ display: 'flex', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10, borderBottom: `2px solid ${theme.colors.primary[200]}` }}>
        <Box sx={{ width: { xs: '40px', md: '80px' }, flexShrink: 0 }} />
        {weekDays.map((d, idx) => {
          const today = d.isSame(moment(), 'day');
          return (
            <Box key={d.format('YYYY-MM-DD')} sx={{ flex: 1, p: { xs: 0.5, md: 1.5 }, textAlign: 'center', backgroundColor: today ? theme.colors.primary[100] : theme.colors.primary[50], borderRight: `1px solid ${theme.colors.primary[100]}`, '&:last-child': { borderRight: 'none' } }}>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.55rem', md: '0.7rem' }, textTransform: 'none', color: theme.colors.text.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t(weekDayKeys[idx])}</Typography>
              <Typography sx={{ fontWeight: today ? 800 : 600, fontSize: { xs: '0.85rem', md: '1.1rem' }, color: today ? theme.colors.primary[700] : theme.colors.text.primary }}>{d.format('D')}</Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex' }}>
        {/* HEURES */}
        <Box sx={{ width: { xs: '40px', md: '80px' }, flexShrink: 0, position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 5, borderRight: `1px solid ${theme.colors.primary[200]}` }}>
          {hours.map((h, i) => (
            <Box key={h} sx={{ position: 'absolute', top: `${i * HOUR_HEIGHT}px`, left: 0, right: 0, height: HOUR_HEIGHT, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 0.5 }}>
              <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' }, color: theme.colors.text.secondary, fontWeight: 500 }}>{`${h}:00`}</Typography>
            </Box>
          ))}
        </Box>

        {/* JOURS */}
        {weekDays.map(d => {
          const key = d.format('YYYY-MM-DD');
          const apps = appointmentsByDay[key] || [];
          const today = d.isSame(moment(), 'day');
          return (
            <Box key={key} sx={{ flex: 1, position: 'relative', minHeight: `${HOUR_HEIGHT * hours.length}px`, borderRight: `1px solid ${theme.colors.primary[100]}`, backgroundColor: today ? 'rgba(66, 133, 244, 0.03)' : '#fafafa', '&:last-child': { borderRight: 'none' } }}>
              {hours.map((h, i) => <Box key={`line-${h}`} sx={{ position: 'absolute', top: `${i * HOUR_HEIGHT}px`, left: 0, right: 0, borderTop: `1px solid ${theme.colors.primary[200]}`, pointerEvents: 'none', zIndex: 1 }} />)}
              {hours.slice(0, -1).map((h, i) => <Box key={`half-${h}`} sx={{ position: 'absolute', top: `${(i + 1) * HOUR_HEIGHT - 1}px`, left: 0, right: 0, borderTop: `1px dashed ${theme.colors.primary[100]}`, pointerEvents: 'none', zIndex: 1 }} />)}

              {apps.map((a, i) => (
                <Tooltip
                  key={`${a.appointment_id}-${i}`}
                  title={
                    <Box sx={{ p: 1.5, minWidth: 240, fontFamily: 'Roboto, sans-serif' }}>
                      {/* CLIENT */}
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: '#fff',
                          fontSize: '1rem',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 18 }} />
                        {a.client_name}
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                        {/* SERVICE */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 4, height: 4, bgcolor: '#fff', borderRadius: '50%' }} />
                          <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                            <strong>{t('pro_dashboard.services.title')}:</strong> {a.service_name}
                          </Typography>
                        </Box>

                        {/* TÉLÉPHONE CLIENT */}
                        {a.client_phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon sx={{ fontSize: 14, color: '#ffcc80' }} />
                            <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                              <strong>{t('pro_dashboard.client.phone')}:</strong> {a.client_phone}
                            </Typography>
                          </Box>
                        )}

                        {/* EMPLOYÉ */}
                        {a.team_member_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 14, color: '#90caf9' }} />
                            <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                              <strong>{t('pro_dashboard.team.title')}:</strong> {a.team_member_name}
                            </Typography>
                          </Box>
                        )}

                        {/* SALON */}
                        {a.salon_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon sx={{ fontSize: 14, color: '#a5d6a7' }} />
                            <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                              <strong>{t('pro_dashboard.salon.title')}:</strong> {a.salon_name}
                            </Typography>
                          </Box>
                        )}

                        {/* HORAIRE */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 28,
                            height: 28,
                            bgcolor: '#fff',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#333',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                          }}>
                            {moment.parseZone(a.start_time).format('HH')}
                          </Box>
                          <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 500 }}>
                            {moment.parseZone(a.start_time).format('HH:mm')} → {moment.parseZone(a.end_time).format('HH:mm')}
                          </Typography>
                        </Box>

                        {/* DURÉE */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`${a.durationMinutes} min`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.2)',
                              color: '#fff',
                              fontWeight: 600,
                              height: 22,
                              fontSize: '0.75rem',
                            }}
                          />
                          <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                            <strong>{t('pro_dashboard.appointments.duration')}</strong>
                          </Typography>
                        </Box>

                        {/* STATUT */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: getStatusColor(a.status),
                              color: '#fff',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                            }}
                          >
                            {getStatusLabel(a.status)}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  }
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#1a1a1a',
                        color: '#fff',
                        borderRadius: 2,
                        boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                        border: '1px solid #333',
                        p: 0,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: `${a.topPosition}px`,
                      left: '4px',
                      right: '4px',
                      height: `${a.height}px`,
                      minHeight: 24,
                      backgroundColor: getStatusColor(a.status),
                      border: `1px solid ${getStatusColor(a.status)}`,
                      borderLeft: `3px solid ${getStatusColor(a.status)}`,
                      borderRadius: 1,
                      p: 0.3,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                      zIndex: 2,
                      '&:hover': { boxShadow: '0 3px 10px rgba(0,0,0,0.2)', zIndex: 10, '& .actions': { opacity: 1 } },
                    }}
                    onClick={() => isMobile ? handleMobileDayClick([a]) : onShowDetails?.(a)}
                  >
                    <Box
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        borderRadius: 0.5,
                        p: 0.2,
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {/* ≤ 30 MIN → 1 LIGNE CENTRÉE */}
                      {a.durationMinutes <= 30 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, flexWrap: 'nowrap', width: '100%' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.65rem', color: theme.colors.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, textAlign: 'center' }}>
                            {a.client_name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.55rem', color: theme.colors.text.secondary }}>
                            {moment.parseZone(a.start_time).format('HH:mm')}
                          </Typography>
                          <Chip label={`${a.durationMinutes}m`} size="small" sx={{ height: 14, fontSize: '0.5rem', backgroundColor: theme.colors.grey[100], color: theme.colors.text.secondary }} />
                        </Box>
                      ) : (
                        /* > 30 MIN → 2 LIGNES */
                        <>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', color: theme.colors.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center', width: '100%' }}>
                            {a.client_name}
                          </Typography>
                          {a.height > 35 && (
                            <Box sx={{ display: 'flex', gap: 0.2, mt: 0.1, justifyContent: 'center', flexWrap: 'wrap', width: '100%' }}>
                              <Typography sx={{ fontSize: '0.55rem', color: theme.colors.text.secondary }}>
                                {moment(a.start_time).format('HH:mm')}
                              </Typography>
                              <Chip label={`${a.durationMinutes}m`} size="small" sx={{ height: 12, fontSize: '0.5rem', backgroundColor: theme.colors.grey[100], color: theme.colors.text.secondary }} />
                              {a.team_member_name && (
                                <Chip
                                  icon={<PersonIcon sx={{ fontSize: 8 }} />}
                                  label={a.team_member_name.split(' ')[0]}
                                  size="small"
                                  sx={{ height: 12, fontSize: '0.5rem', backgroundColor: theme.colors.primary[100], color: theme.colors.primary[700] }}
                                />
                              )}
                            </Box>
                          )}
                        </>
                      )}

                      {/* BOUTONS D'ACTION */}
                      <Box className="actions" sx={{ position: 'absolute', top: 1, right: 1, display: 'flex', gap: 0.2, opacity: 0, transition: 'opacity 0.2s' }}>
                        <MarkAttendance appointment={a} onUpdate={() => window.location.reload()} />
                        <IconButton 
                          size="small" 
                          onClick={(e) => { e.stopPropagation(); onShowDetails?.(a); }} 
                          sx={{ p: 0.1, backgroundColor: 'rgba(255,255,255,0.9)', width: 14, height: 14, minWidth: 'unset', color: theme.colors.primary[600], '&:hover': { backgroundColor: theme.colors.primary[500], color: '#fff' } }}
                        >
                          <Visibility sx={{ fontSize: 10 }} />
                        </IconButton>
                        {!isEmployee && (
                          <>
                            <IconButton 
                              size="small" 
                              onClick={(e) => { e.stopPropagation(); onEditAppointment?.(a); }} 
                              sx={{ p: 0.1, backgroundColor: 'rgba(255,255,255,0.9)', width: 14, height: 14, minWidth: 'unset', color: theme.colors.warning[600], '&:hover': { backgroundColor: theme.colors.warning[500], color: '#fff' } }}
                            >
                              <Edit sx={{ fontSize: 10 }} />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={(e) => { e.stopPropagation(); onDeleteAppointment?.(a); }} 
                              sx={{ p: 0.1, backgroundColor: 'rgba(255,255,255,0.9)', width: 14, height: 14, minWidth: 'unset', color: theme.colors.error[600], '&:hover': { backgroundColor: theme.colors.error[500], color: '#fff' } }}
                            >
                              <Delete sx={{ fontSize: 10 }} />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          );
        })}
      </Box>

      {/* Dialog mobile pour afficher les RDV du jour */}
      <Dialog
        open={mobileDialogOpen}
        onClose={() => {
          setMobileDialogOpen(false);
          setSelectedAppointmentForAction(null);
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiDialog-paper': {
            m: { xs: 0, md: 2 },
            maxHeight: { xs: '100%', md: 'calc(100% - 64px)' },
            width: { xs: '100%', md: 'auto' },
            maxWidth: { xs: '100%', md: '600px' },
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          borderBottom: `2px solid ${theme.colors.primary[100]}`,
        }}>
          {t('pro_dashboard.appointments.day_appointments')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedDayAppointments.map((appointment, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: `2px solid ${getStatusColor(appointment.status)}`,
                backgroundColor: '#f9f9f9',
                transition: 'all 0.2s ease',
                position: 'relative',
                '&:hover': {
                  backgroundColor: theme.colors.primary[50],
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }
              }}
            >
              {/* Boutons d'action en haut à droite */}
              {!isEmployee && (
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                  <Tooltip title={t('actions.edit')}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditAppointment?.(appointment);
                        setMobileDialogOpen(false);
                        setSelectedAppointmentForAction(null);
                      }}
                      sx={{
                        color: theme.colors.primary[500],
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          backgroundColor: theme.colors.primary[500],
                          color: '#fff',
                        }
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('actions.delete')}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteAppointment?.(appointment);
                        setMobileDialogOpen(false);
                        setSelectedAppointmentForAction(null);
                      }}
                      sx={{
                        color: theme.colors.error[500],
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          backgroundColor: theme.colors.error[500],
                          color: '#fff',
                        }
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, pr: 8 }}>
                {moment(appointment.start_time).format('HH:mm')} - {appointment.client_name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>{t('pro_dashboard.services.title')}:</strong> {appointment.service_name}
              </Typography>
              {appointment.team_member_name && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t('pro_dashboard.team.title')}:</strong> {appointment.team_member_name}
                </Typography>
              )}
              {appointment.salon_name && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t('pro_dashboard.salon.title')}:</strong> {appointment.salon_name}
                </Typography>
              )}
              <Chip
                label={getStatusLabel(appointment.status)}
                size="small"
                sx={{
                  backgroundColor: getStatusColor(appointment.status),
                  color: 'white',
                  mt: 1,
                }}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMobileDialogOpen(false);
            setSelectedAppointmentForAction(null);
          }}>{t('actions.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeekView;